import { useState } from "react";
import { GlobeIcon } from "src/lib/ui-icon-v2";
import { DropdownMenu } from "src/components/DropdownMenu/DropdownMenu.tsx";
import { useList } from "src/system/useList.tsx";
import { ResourceEnum } from "src/constants/constants.ts";
import type { LanguageResource } from "@exonn/js-sdk";
import { LanguageChangeConfirmModal } from "src/components/Form/LanguageSelect/LanguageChangeConfirmModal.tsx";

type Option = {
  label: string;
  value: string;
  exists: boolean;
};

type LanguageSelectProps = {
  availableLocales: string[];
  value: string;
  disabled?: boolean;
  onChange?: (params: { lang: string; copy: boolean }) => void;
};

export function LanguageSelect(props: LanguageSelectProps) {
  const { value, disabled = false, onChange, availableLocales } = props;
  const { data, refetch } = useList<LanguageResource>({
    resource: ResourceEnum.Language,
  });

  const options: Option[] = (data?.data || []).map((language) => {
    return {
      label: language.title,
      value: language.locale,
      exists: availableLocales.includes(language.locale),
    };
  });

  const [pendingLanguage, setPendingLanguage] = useState<{
    value: string;
    label: string;
  }>();

  const handleChange = (language: { value: string; label: string }) => {
    const noMatches = options
      .filter((option) => option.exists)
      .every((option) => option.value !== language.value);

    if (noMatches) {
      setPendingLanguage(language);
    } else {
      onChange?.({
        lang: language.value,
        copy: false,
      });
    }
  };

  const renderLangTitle = (title: string, locale: string) => {
    const exists = options
      .filter((option) => option.exists)
      .map((option) => option.value)
      .includes(locale);
    return (
      <span
        style={{
          opacity: exists ? 1 : 0.65,
        }}
      >
        {title}
      </span>
    );
  };

  const dropdownItems = options.map((option) => ({
    value: option.value,
    label: renderLangTitle(option.label, option.value),
    onSelect: () => handleChange(option),
  }));

  const onConfirm = (copy: boolean) => {
    if (!pendingLanguage) {
      throw new Error("pending language must be set");
    }
    setPendingLanguage(undefined);
    onChange?.({
      lang: pendingLanguage.value,
      copy,
    });
  };

  const handleModalClose = () => {
    setPendingLanguage(undefined);
  };

  const handleOpen = (open: boolean) => {
    if (open) {
      refetch();
    }
  };

  // todo: implement error state and loading state

  return (
    <>
      {pendingLanguage && (
        <LanguageChangeConfirmModal
          language={pendingLanguage}
          onClose={handleModalClose}
          onConfirm={onConfirm}
        />
      )}
      <DropdownMenu
        css={{ width: 76 }}
        items={dropdownItems}
        value={value}
        contentAlign="end"
        disabled={disabled}
        onOpenChange={handleOpen}
      >
        <DropdownMenu.Trigger>
          <GlobeIcon />
          <DropdownMenu.Label>{value.toUpperCase()}</DropdownMenu.Label>
          <DropdownMenu.Arrow />
        </DropdownMenu.Trigger>
      </DropdownMenu>
    </>
  );
}
